import { useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { listPeople } from '@/data/queries'
import { useAsync } from '@/data/useAsync'
import { Card, Failed, Loading, ProvenanceDate, StatusPill } from '@/components/bits'
import { useOrg } from '@/org/OrgProvider'

export function People() {
  const { current } = useOrg()
  const navigate = useNavigate()
  const [dept, setDept] = useState('all')
  const [search, setSearch] = useState('')

  const result = useAsync(() => listPeople(current.id), [current.id])

  const departments = useMemo(() => {
    if (result.state !== 'ready') return []
    const counts = new Map<string, number>()
    for (const r of result.data) {
      const key = r.department ?? 'No department'
      counts.set(key, (counts.get(key) ?? 0) + 1)
    }
    return [...counts.entries()].sort((a, b) => b[1] - a[1])
  }, [result.state, result.state === 'ready' ? result.data : null])

  if (result.state === 'loading') return <Loading what="people" />
  if (result.state === 'error') return <Failed message={result.message} onRetry={result.reload} />

  const term = search.trim().toLowerCase()
  const rows = result.data.filter((r) => {
    const inDept = dept === 'all' || (r.department ?? 'No department') === dept
    const matches = term === '' || r.name.toLowerCase().includes(term) || (r.reference ?? '').toLowerCase().includes(term)
    return inDept && matches
  })

  return (
    <div className="screen">
      <Card
        title="People"
        flush
        aside={
          <>
            <span className="eyebrow">
              {rows.length} of {result.data.length}
            </span>
            <button className="btn sm" type="button" disabled title="Not built yet">
              Add starter
            </button>
            <button className="btn sm quiet" type="button" disabled title="Not built yet">
              Import CSV
            </button>
          </>
        }
      >
        <div className="filters">
          <button type="button" aria-pressed={dept === 'all'} onClick={() => setDept('all')}>
            All<span className="n">{result.data.length}</span>
          </button>
          {departments.map(([name, n]) => (
            <button key={name} type="button" aria-pressed={dept === name} onClick={() => setDept(name)}>
              {name}
              <span className="n">{n}</span>
            </button>
          ))}
          <input
            type="search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search name or reference"
            aria-label="Search people"
            style={{
              marginLeft: 'auto',
              padding: '4px 13px',
              font: 'inherit',
              fontSize: 12.5,
              color: 'var(--ivory)',
              background: 'var(--ground)',
              border: '1px solid var(--line)',
              borderRadius: 'var(--r-pill)',
            }}
          />
        </div>

        <div className="tablewrap">
          <table>
            <thead>
              <tr>
                <th>Name</th>
                <th>Ref</th>
                <th>Department</th>
                <th>Position</th>
                <th>Start date</th>
                <th>Status</th>
                <th className="r">Flags</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr
                  key={r.employmentId}
                  className="clickable"
                  onClick={() => navigate(`/people/${r.employmentId}`)}
                >
                  <td className="name">{r.name}</td>
                  <td className="mono">{r.reference ?? '—'}</td>
                  <td>{r.department ?? '—'}</td>
                  <td style={{ color: 'var(--ivory-dim)' }}>{r.position ?? '—'}</td>
                  <td>
                    <ProvenanceDate iso={r.startDate} source={r.startDateSource} />
                  </td>
                  <td>
                    <StatusPill status={r.status} />
                  </td>
                  <td className="r">
                    {r.openFlags === 0 ? (
                      <span style={{ color: 'var(--ivory-faint)' }}>—</span>
                    ) : (
                      <span className="mono">{r.openFlags}</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div
          style={{
            padding: '14px 18px',
            borderTop: '1px solid var(--line-soft)',
            fontSize: 12.5,
            color: 'var(--ivory-faint)',
            display: 'flex',
            flexWrap: 'wrap',
            gap: 16,
          }}
        >
          <span>Date marker:</span>
          <span className="prov integration" style={{ color: 'var(--ivory-dim)' }}>
            from Planday
          </span>
          <span className="prov defaulted" style={{ color: 'var(--ivory-dim)' }}>
            defaulted
          </span>
          <span className="prov hr_confirmed" style={{ color: 'var(--ivory-dim)' }}>
            confirmed by HR
          </span>
        </div>
      </Card>
    </div>
  )
}
